import React from 'react';
import { formatCurrency } from '../../../utils/formatters';

/**
 * ReturnItemInfo – uniform header section for return-related modals.
 * Props:
 *  - item (required): orderItem object containing product, quantity, size, price.
 *  - reasonText (optional): string to display as reason line.
 *  - statusText (optional): string badge to display instead of reason.
 *  - showReturnDetails (optional): boolean to control visibility of return-specific details
 */
const ReturnItemInfo = ({ item, statusText, showReturnDetails = true }) => {
  if (!item) {
    return <div className="text-red-500 text-sm">Item data missing</div>;
  }

  // Extract product data
  const { product, price, quantity, currency = 'USD', status } = item;
  
  // Extract return request details
  const returnRequest = item.returnRequest || {};
  const { reason, requestDetails, usageFee } = returnRequest;
  
  // Extract reason safely
  const extractReason = (details) => {
    if (!details) return undefined;
    if (typeof details === 'string') {
      try { 
        const parsed = JSON.parse(details);
        return parsed.reason; 
      } catch { 
        return undefined; 
      }
    }
    return details.reason;
  };
  
  const displayReason = reason || 
    extractReason(requestDetails) || 
    'Not specified';
  
  // Format the reason text for display
  const formatReason = (rawReason) => {
    if (!rawReason) return 'Not specified';
    return rawReason
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };
  
  // Calculate how many days the customer has had the product
  const deliveryTimestamp = item?.deliveredAt || item?.delivered_at;
  let daysHeldDisplay = 'N/A';
  
  if (deliveryTimestamp) {
    const deliveredDate = new Date(deliveryTimestamp);
    if (!isNaN(deliveredDate.getTime())) {
      const diffMs = Date.now() - deliveredDate.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);

      if (diffHours < 24) {
        const hrs = Math.floor(diffHours);
        daysHeldDisplay = `${hrs} hour${hrs !== 1 ? 's' : ''}`;
      } else {
        const diffDays = Math.floor(diffHours / 24);
        daysHeldDisplay = `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
      }
    }
  }
  
  // Determine if this item is part of a return
  const isReturnItem = !!returnRequest.id || 
                       ['return_requested_by_customer', 'return_approved_by_merchant'].includes(status);

  return (
    <div className="flex flex-col md:flex-row md:items-start gap-4">
      {/* Product Image */}
      <div className="w-24 h-24 flex-shrink-0">
        {product?.photos?.[0]?.url ? (
          <img 
            src={product.photos[0].url} 
            alt={product.name} 
            className="w-full h-full object-cover rounded-md"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-md">
            <span className="text-gray-400 text-xs">No image</span>
          </div>
        )}
      </div>
      
      {/* Product Details */}
      <div className="flex-grow">
        <h3 className="font-medium text-gray-900">{product?.name || 'Product'}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mt-1">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Price:</span> {formatCurrency(price, currency)}
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Quantity:</span> {quantity || 1}
          </p>
          {/* <p className="text-sm text-gray-600">
            <span className="font-medium">Status:</span> {item.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </p> */}
          {/* <p className="text-sm text-gray-600">
            <span className="font-medium">Held for:</span> {daysHeldDisplay}
          </p> */}
          {/* <p className="text-sm text-gray-600 md:col-span-2">
            <span className="font-medium">Return reason:</span> {formatReason(displayReason)}
          </p> */}
          
          {/* Show comments if available */}
          {returnRequest.comments && (
            <p className="text-sm text-gray-600 md:col-span-2">
              <span className="font-medium">Comments:</span> {returnRequest.comments}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReturnItemInfo; 